import React from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import {
    ChevronLeft as PreviousIcon,
    ChevronRight as NextIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { useTenantContext } from '../../context/TenantContext';

const formatMoney = val => Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
});

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const PayslipPreviewDialog = ({ open, payslip, run, onClose, onPrevious, onNext, position = 0, total = 0 }) => {
    const { tenantProfile } = useTenantContext();

    if (!payslip) return null;

    const isOfficial = ['FINALIZED', 'PAID'].includes(run?.status);
    const periodMonth = Number(run?.period_month || payslip.period_month || 1);
    const periodYear = run?.period_year || payslip.period_year;
    const periodName = `${monthNames[periodMonth - 1] || periodMonth} ${periodYear || ''}`;

    const grossPay = Number(payslip.gross_pay || 0);
    const netPay = Number(payslip.net_pay || 0);
    const totalDeductions = Math.max(0, grossPay - netPay);

    // Build earnings items
    const earnings = [];
    if (Number(payslip.basic_salary || 0) > 0) {
        earnings.push({ label: 'Basic Salary', amount: Number(payslip.basic_salary) });
    }
    if (Number(payslip.ot_pay || 0) > 0) {
        const otHours = Number(payslip.payable_ot_mins || 0) > 0
            ? ` (${(Number(payslip.payable_ot_mins) / 60).toFixed(1)} hrs)`
            : '';
        earnings.push({ label: `Overtime Pay${otHours}`, amount: Number(payslip.ot_pay) });
    }
    if (Number(payslip.meal_allowance || 0) > 0) earnings.push({ label: 'Meal Allowance', amount: Number(payslip.meal_allowance) });
    if (Number(payslip.transport_allowance || 0) > 0) earnings.push({ label: 'Transport Allowance', amount: Number(payslip.transport_allowance) });
    if (Number(payslip.special_allowance || 0) > 0) earnings.push({ label: 'Special Allowance', amount: Number(payslip.special_allowance) });
    if (Number(payslip.ot_allowance || 0) > 0) earnings.push({ label: 'Shift OT Allowance', amount: Number(payslip.ot_allowance) });

    const recurringEarnings = (payslip.recurring_items || []).filter(
        it => it.element_type === 'EARNING' && !['BASIC', 'OT'].includes(it.element_code)
    );
    recurringEarnings.forEach(it => {
        if (Number(it.amount || 0) > 0) {
            earnings.push({ label: it.element_name || it.element_code, amount: Number(it.amount) });
        }
    });

    const recurringEarningsSum = recurringEarnings.reduce((acc, it) => acc + Number(it.amount || 0), 0);
    const unallocatedOtherEarnings = Math.max(0, Number(payslip.other_earnings || 0) - recurringEarningsSum);
    if (unallocatedOtherEarnings > 0) {
        earnings.push({ label: 'Other Earnings', amount: unallocatedOtherEarnings });
    }

    // Build deductions items
    const deductions = [];
    if (Number(payslip.late_early_penalties || 0) > 0) {
        deductions.push({ label: 'Late / Early Penalties', amount: Number(payslip.late_early_penalties) });
    }
    if (Number(payslip.unpaid_leave_deduction || 0) > 0) {
        const days = Number(payslip.unpaid_leave_days || 0) > 0 ? ` (${payslip.unpaid_leave_days} d)` : '';
        deductions.push({ label: `Unpaid Leave${days}`, amount: Number(payslip.unpaid_leave_deduction) });
    }
    if (Number(payslip.unpaid_absence_deduction || 0) > 0) {
        const days = Number(payslip.unpaid_absence_days || 0) > 0 ? ` (${payslip.unpaid_absence_days} d)` : '';
        deductions.push({ label: `Unpaid Absence${days}`, amount: Number(payslip.unpaid_absence_deduction) });
    }
    if (Number(payslip.ssb_deduction || 0) > 0) {
        deductions.push({ label: 'Social Security (SSB 2%)', amount: Number(payslip.ssb_deduction) });
    }
    if (Number(payslip.tax_deduction || 0) > 0) {
        deductions.push({ label: 'Income Tax (PIT)', amount: Number(payslip.tax_deduction) });
    }
    if (Number(payslip.advance_deduction || 0) > 0) {
        deductions.push({ label: 'Advance Deduction', amount: Number(payslip.advance_deduction) });
    }

    const recurringDeductions = (payslip.recurring_items || []).filter(
        it => it.element_type === 'DEDUCTION'
    );
    recurringDeductions.forEach(it => {
        if (Number(it.amount || 0) > 0) {
            deductions.push({ label: it.element_name || it.element_code, amount: Number(it.amount) });
        }
    });

    const recurringDeductionsSum = recurringDeductions.reduce((acc, it) => acc + Number(it.amount || 0), 0);
    const unallocatedOtherDeductions = Math.max(0, Number(payslip.other_deductions || 0) - recurringDeductionsSum);
    if (unallocatedOtherDeductions > 0) {
        deductions.push({ label: 'Other Deductions', amount: unallocatedOtherDeductions });
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
            <DialogTitle sx={{ p: 0 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ px: 3, py: 1.75, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}
                >
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            Payslip Preview — {payslip.full_name || ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {payslip.employee_number || payslip.staff_code || ''} · {payslip.job_title || ''} {payslip.dept_name ? `(${payslip.dept_name})` : ''} · Period: {periodName}
                        </Typography>
                    </Box>
                    <Chip
                        label={isOfficial ? 'OFFICIAL PAYSLIP' : 'PREVIEW — NOT FINAL'}
                        color={isOfficial ? 'primary' : 'warning'}
                        size="small"
                        sx={{ fontWeight: 800, letterSpacing: '0.03em' }}
                    />
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
                <Paper elevation={0} sx={{ p: 3.5, border: '1px solid #e2e8f0', borderRadius: 3, bgcolor: '#ffffff' }}>
                    {/* Header: Branding + Title */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 2.5, mb: 2.5, borderBottom: '2px solid #0f172a' }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            {tenantProfile?.logo_url && (
                                <Box
                                    component="img"
                                    src={tenantProfile.logo_url}
                                    alt="Logo"
                                    sx={{ height: 44, maxWidth: 120, objectFit: 'contain' }}
                                />
                            )}
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: tenantProfile?.primary_color || '#0f172a', lineHeight: 1.2 }}>
                                    {tenantProfile?.tenant_name || 'PeopleHub'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                                    PAY ADVICE / PAYSLIP
                                </Typography>
                            </Box>
                        </Stack>
                        <Box sx={{ textAlign: 'right' }}>
                            <Chip
                                label={payslip.release_status || (isOfficial ? 'RELEASED' : 'PREVIEW')}
                                color={payslip.release_status === 'RELEASED' || isOfficial ? 'primary' : 'default'}
                                size="small"
                                sx={{ fontWeight: 700, mb: 0.5 }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                Period: {periodName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Version: v{payslip.version_number || 1}
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Employee Details Box */}
                    <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2, mb: 3, border: '1px solid #e2e8f0' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Employee Name</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                    {payslip.full_name || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Employee Code</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                    {payslip.employee_number || payslip.staff_code || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Organization</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                    {payslip.dept_name || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Designation</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                    {payslip.job_title || '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* 2-Column Earnings & Deductions Tables */}
                    <Grid container spacing={2.5}>
                        {/* Earnings Column */}
                        <Grid item xs={12} md={6}>
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#ecfdf5' }}>
                                            <TableCell sx={{ fontWeight: 800, color: '#065f46' }}>Earnings & Allowances</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: '#065f46' }}>Amount (MMK)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {earnings.length > 0 ? (
                                            earnings.map((item, idx) => (
                                                <TableRow key={`earn-${idx}`} sx={{ '&:nth-of-type(even)': { bgcolor: '#f8fafc' } }}>
                                                    <TableCell sx={{ py: 0.8, color: '#334155' }}>{item.label}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(item.amount)}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                                                    No earnings recorded
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                                            <TableCell sx={{ fontWeight: 800, color: '#166534' }}>Total Gross Earnings</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: '#166534', whiteSpace: 'nowrap' }}>
                                                {formatMoney(grossPay)} MMK
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                </Table>
                            </TableContainer>
                        </Grid>

                        {/* Deductions Column */}
                        <Grid item xs={12} md={6}>
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#fef2f2' }}>
                                            <TableCell sx={{ fontWeight: 800, color: '#991b1b' }}>Deductions & Taxes</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: '#991b1b' }}>Amount (MMK)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {deductions.length > 0 ? (
                                            deductions.map((item, idx) => (
                                                <TableRow key={`ded-${idx}`} sx={{ '&:nth-of-type(even)': { bgcolor: '#f8fafc' } }}>
                                                    <TableCell sx={{ py: 0.8, color: '#334155' }}>{item.label}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(item.amount)}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                                                    No deductions applied
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#fff1f2' }}>
                                            <TableCell sx={{ fontWeight: 800, color: '#be123c' }}>Total Deductions</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: '#be123c', whiteSpace: 'nowrap' }}>
                                                {formatMoney(totalDeductions)} MMK
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>

                    {/* Net Take-Home Pay Banner */}
                    <Box sx={{ mt: 3, p: 2.5, borderRadius: 2.5, bgcolor: '#ffffff', border: '1.5px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#475569', fontWeight: 800 }}>
                                Net Take-Home Pay
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
                                Gross Pay ({formatMoney(grossPay)}) − Total Deductions ({formatMoney(totalDeductions)})
                            </Typography>
                        </Box>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>
                            {formatMoney(netPay)}{' '}
                            <Typography component="span" variant="h6" sx={{ color: '#64748b', fontWeight: 700 }}>
                                MMK
                            </Typography>
                        </Typography>
                    </Box>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', bgcolor: '#ffffff' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button onClick={onPrevious} disabled={position <= 0} startIcon={<PreviousIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Previous
                    </Button>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, px: 1 }}>
                        {total > 0 ? `${position + 1} of ${total}` : ''}
                    </Typography>
                    <Button onClick={onNext} disabled={position >= total - 1} endIcon={<NextIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Next
                    </Button>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Print Preview
                    </Button>
                    <Button onClick={onClose} sx={{ color: '#64748b', fontWeight: 700, textTransform: 'none' }}>
                        Close
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default PayslipPreviewDialog;

